import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Wrench,
  RotateCcw,
  Download,
  FileText,
  Play,
  AlertCircle,
  CheckCircle2,
  Loader,
  ExternalLink,
  Settings,
  RefreshCw,
  Zap
} from 'lucide-react';
import api from '../services/api';
import LogsDownload from './LogsDownload';

const UnifiedStationActions = ({ station, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('quick');
  const [actionParams, setActionParams] = useState({});
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    commandType: '',
    status: '',
    page: 1,
    limit: 20
  });

  // Helper function to format ISO timestamps to local date/time
  const formatLocalDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('fr-BE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return isoString;
    }
  };


  // Actions rapides (sans paramètres)
  const quickActions = [
    {
      id: 'get_status',
      label: 'Vérifier Statut',
      icon: <Activity className="h-4 w-4" />,
      color: 'blue',
      description: 'État en temps réel de tous les services'
    },
    {
      id: 'run_diagnostic',
      label: 'Diagnostic',
      icon: <Wrench className="h-4 w-4" />,
      color: 'purple',
      description: 'Analyse complète des performances'
    }
  ];

  // Actions avancées (avec paramètres)
  const advancedActions = [
    {
      id: 'restart_service',
      label: 'Redémarrer Service',
      icon: <RotateCcw className="h-4 w-4" />,
      color: 'orange',
      description: 'Redémarrer un service spécifique',
      params: [
        { 
          name: 'service_name', 
          label: 'Service à redémarrer', 
          type: 'select', 
          options: ['iotbox', 'nginx', 'worldline', 'postgres', 'redis'],
          required: true
        }
      ]
    },
    {
      id: 'update_service',
      label: 'Mettre à Jour Service',
      icon: <Download className="h-4 w-4" />,
      color: 'green',
      description: 'Mettre à jour avec une nouvelle version',
      params: [
        { 
          name: 'service_name', 
          label: 'Service à mettre à jour', 
          type: 'select',
          options: ['iotbox', 'nginx', 'worldline'],
          required: true
        },
        { 
          name: 'version', 
          label: 'Version cible', 
          type: 'text', 
          default: 'latest',
          placeholder: 'latest, v1.2.3, etc.'
        }
      ]
    }
  ];

  const executeQuickAction = async (action) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const actionData = {
        command_type: action.id,
        command_data: {},
        priority: 'medium',
        max_retries: 2,
        timeout_seconds: 60
      };

      const response = await api.post(`/api/v1/stations/${station.station_id}/commands`, actionData);
      
      setResult({
        type: 'success',
        message: `Action "${action.label}" exécutée avec succès`,
        action: response.data
      });

    } catch (error) {
      setError({
        type: 'error',
        message: error.response?.data?.detail || error.message || 'Erreur lors de l\'exécution'
      });
    } finally {
      setLoading(false);
    }
  };

  const executeAdvancedAction = async (actionType) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const commandData = {};
      
      actionType.params.forEach(param => {
        const value = actionParams[`${actionType.id}_${param.name}`];
        if (value !== undefined && value !== '') {
          commandData[param.name] = param.type === 'number' ? parseInt(value) : value;
        } else if (param.default) {
          commandData[param.name] = param.type === 'number' ? parseInt(param.default) : param.default;
        }
      });

      const actionData = {
        command_type: actionType.id,
        command_data: commandData,
        priority: 'medium',
        max_retries: 2,
        timeout_seconds: actionType.id === 'update_service' ? 300 : 60
      };

      const response = await api.post(`/api/v1/stations/${station.station_id}/commands`, actionData);
      
      setResult({
        type: 'success',
        message: `Action "${actionType.label}" créée avec succès`,
        action: response.data
      });

      setActionParams({});

    } catch (error) {
      setError({
        type: 'error',
        message: error.response?.data?.detail || error.message || 'Erreur lors de l\'exécution'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (actionId, paramName, value) => {
    setActionParams(prev => ({
      ...prev,
      [`${actionId}_${paramName}`]: value
    }));
  };

  const loadHistoryData = useCallback(async (filters = null) => {
    const actualFilters = filters || historyFilters;
    setHistoryLoading(true);
    try {
      const params = {
        station_id: station.station_id,
        limit: actualFilters.limit,
        offset: (actualFilters.page - 1) * actualFilters.limit
      };

      if (actualFilters.commandType) {
        params.command_type = actualFilters.commandType;
      }
      if (actualFilters.status) {
        params.status = actualFilters.status;
      }

      const response = await api.get('/api/v1/tracking/actions/history', { params });
      setHistoryData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique des actions:', error);
      setError({
        type: 'error',
        message: 'Erreur lors du chargement de l\'historique des actions'
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [station.station_id, historyFilters]);

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...historyFilters,
      [filterType]: value,
      page: 1 // Reset to first page when filtering
    };
    setHistoryFilters(newFilters);
    loadHistoryData(newFilters);
  };

  const handlePageChange = (newPage) => {
    const newFilters = {
      ...historyFilters,
      page: newPage
    };
    setHistoryFilters(newFilters);
    loadHistoryData(newFilters);
  };

  // Charger automatiquement les données de l'historique quand l'onglet est sélectionné
  useEffect(() => {
    if (activeTab === 'tracking' && !historyData && !historyLoading) {
      loadHistoryData();
    }
  }, [activeTab, historyData, historyLoading, loadHistoryData]);

  const renderActionParams = (actionType) => {
    if (!actionType.params || actionType.params.length === 0) return null;

    return (
      <div className="space-y-3 mt-4">
        {actionType.params.map(param => (
          <div key={param.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {param.label} {param.required && <span className="text-red-500">*</span>}
            </label>
            {param.type === 'select' ? (
              <select
                value={actionParams[`${actionType.id}_${param.name}`] || param.default || ''}
                onChange={(e) => handleParamChange(actionType.id, param.name, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required={param.required}
              >
                <option value="">Sélectionner...</option>
                {param.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                type={param.type}
                value={actionParams[`${actionType.id}_${param.name}`] || param.default || ''}
                onChange={(e) => handleParamChange(actionType.id, param.name, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={param.placeholder || param.label}
                required={param.required}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  if (!station) {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-center space-x-2 text-gray-500">
          <AlertCircle className="h-6 w-6" />
          <span className="text-lg">Station non disponible</span>
        </div>
        <p className="text-center text-gray-400 mt-2">
          Impossible de charger les informations de la station
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* En-tête avec onglets */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6 pt-4">
          {[
            { id: 'quick', label: 'Actions Rapides', icon: <Zap className="h-4 w-4" /> },
            { id: 'advanced', label: 'Maintenance', icon: <Settings className="h-4 w-4" /> },
            { id: 'logs', label: 'Logs & Télémétrie', icon: <FileText className="h-4 w-4" /> },
            { id: 'tracking', label: 'Historique', icon: <Activity className="h-4 w-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {/* Messages de résultat/erreur */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">{result.message}</p>
                {result.action && (
                  <p className="text-xs text-green-600 mt-1">ID: {result.action.id}</p>
                )}
                {result.data && result.data.grafana_url && (
                  <div className="mt-3">
                    <a
                      href={result.data.grafana_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-sm bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>Voir dans Grafana</span>
                    </a>
                  </div>
                )}
              </div>
              <button 
                onClick={() => setResult(null)}
                className="text-green-400 hover:text-green-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error.message}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Contenu selon l'onglet actif */}
        {activeTab === 'quick' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Immédiates</h3>
            {station.status === 'offline' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      <strong>Station hors ligne :</strong> Les actions en temps réel ne sont pas disponibles. 
                      Utilisez l'onglet "Logs & Télémétrie" pour accéder aux données historiques.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quickActions.map(action => (
                <button
                  key={action.id}
                  onClick={() => executeQuickAction(action)}
                  disabled={loading || station.status === 'offline'}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 hover:shadow-md ${
                    loading || station.status === 'offline'
                      ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50' 
                      : `border-${action.color}-200 bg-${action.color}-50 hover:bg-${action.color}-100 hover:border-${action.color}-300`
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 bg-${action.color}-100 rounded-lg`}>
                      {action.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{action.label}</h4>
                      <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                      {station.status === 'offline' && (
                        <p className="text-xs text-red-500 mt-1">Non disponible hors ligne</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Maintenance Avancée</h3>
            {station.status === 'offline' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      <strong>Station hors ligne :</strong> Les actions de maintenance ne sont pas disponibles. 
                      Utilisez l'onglet "Logs & Télémétrie" pour accéder aux données historiques.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {advancedActions.map(actionType => (
              <div key={actionType.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start space-x-3 mb-4">
                  <div className={`p-2 bg-${actionType.color}-100 rounded-lg`}>
                    {actionType.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{actionType.label}</h4>
                    <p className="text-sm text-gray-600">{actionType.description}</p>
                    {station.status === 'offline' && (
                      <p className="text-xs text-red-500 mt-1">Non disponible hors ligne</p>
                    )}
                  </div>
                </div>
                
                {renderActionParams(actionType)}

                <button
                  onClick={() => executeAdvancedAction(actionType)}
                  disabled={loading || station.status === 'offline'}
                  className={`mt-4 w-full px-4 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 ${
                    loading || station.status === 'offline'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : `bg-${actionType.color}-500 hover:bg-${actionType.color}-600 text-white`
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Exécution...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Play className="h-4 w-4" />
                      <span>Exécuter {actionType.label}</span>
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'logs' && (
          <LogsDownload station={station} />
        )}

        {activeTab === 'tracking' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Historique des Actions</h3>
              <div className="flex items-center space-x-2">
                {/* Filtres */}
                <select
                  value={historyFilters.commandType}
                  onChange={(e) => handleFilterChange('commandType', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous les types</option>
                  <option value="get_status">get_status</option>
                  <option value="restart_service">restart_service</option>
                  <option value="run_diagnostic">run_diagnostic</option>
                  <option value="check_health">check_health</option>
                </select>
                
                <select
                  value={historyFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="launched">Lancé</option>
                  <option value="consumed">Consommé</option>
                  <option value="completed">Terminé</option>
                  <option value="failed">Échoué</option>
                </select>
                
                <button
                  onClick={() => loadHistoryData()}
                  disabled={historyLoading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
                >
                  <RefreshCw className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />
                  <span>Actualiser</span>
                </button>
              </div>
            </div>

            {historyLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-600">Chargement de l'historique...</span>
              </div>
            )}

            {historyData && !historyLoading && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lancé
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Consommé
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Terminé
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historyData.actions.map((action) => (
                        <tr key={action.action_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                            {action.action_id.substring(0, 8)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {action.command_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatLocalDateTime(action.launched_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatLocalDateTime(action.consumed_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatLocalDateTime(action.completed_at || action.failed_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              action.status === 'completed' ? 'bg-green-100 text-green-800' :
                              action.status === 'failed' ? 'bg-red-100 text-red-800' :
                              action.status === 'consumed' ? 'bg-blue-100 text-blue-800' :
                              action.status === 'launched' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {action.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {historyData.actions.length === 0 && (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune action trouvée</p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {historyData && historyData.total_pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(historyData.page - 1)}
                      disabled={!historyData.has_prev}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => handlePageChange(historyData.page + 1)}
                      disabled={!historyData.has_next}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Affichage de <span className="font-medium">{((historyData.page - 1) * historyData.limit) + 1}</span> à{' '}
                        <span className="font-medium">
                          {Math.min(historyData.page * historyData.limit, historyData.total)}
                        </span>{' '}
                        sur <span className="font-medium">{historyData.total}</span> résultats
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(historyData.page - 1)}
                          disabled={!historyData.has_prev}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Précédent</span>
                          ←
                        </button>
                        
                        {/* Pages */}
                        {Array.from({ length: Math.min(5, historyData.total_pages) }, (_, i) => {
                          const pageNum = Math.max(1, historyData.page - 2) + i;
                          if (pageNum > historyData.total_pages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === historyData.page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(historyData.page + 1)}
                          disabled={!historyData.has_next}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Suivant</span>
                          →
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}

            {!historyData && !historyLoading && (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune donnée d'historique disponible</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedStationActions;
